import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/authService';
import { Trans, useLingui } from '@lingui/react/macro';
import { useTheme } from '../contexts/ThemeContext';

// Mirror of the API's password-validator, matching SignupPage's checklist.
const passwordRules = (pw: string) => [
  { id: 'length', met: pw.length >= 8 },
  { id: 'uppercase', met: /[A-Z]/.test(pw) },
  { id: 'lowercase', met: /[a-z]/.test(pw) },
  { id: 'number', met: /[0-9]/.test(pw) },
];

type Status = 'checking' | 'ready' | 'invalid' | 'expired' | 'success';

/**
 * Landing page for the emailed reset link (/reset-password?token=...).
 *
 * Token hygiene: the token is captured once on mount and immediately stripped
 * from the address bar (history.replaceState), so the interactive page URL —
 * and anything that sees it (browser history, Referer) — never carries it. It
 * is only ever sent in POST bodies. A referrer meta tag is applied for the
 * page's lifetime as an extra layer, and validation is non-consuming: only the
 * final submit burns the token, so mail-scanner prefetches can't kill the link.
 */
export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useLingui();
  const { isDark } = useTheme();
  const tokenRef = useRef<string | null>(null);
  const [status, setStatus] = useState<Status>('checking');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Keep the token out of the Referer header for the page's lifetime.
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    const token = searchParams.get('token');
    if (token && !tokenRef.current) {
      tokenRef.current = token;
      // Strip the token from the visible URL before anything else happens.
      window.history.replaceState(null, '', window.location.pathname);
    }
    if (!tokenRef.current) {
      setStatus('invalid');
      return;
    }

    let cancelled = false;
    (async () => {
      const result = await authService.validateResetToken(tokenRef.current!);
      if (cancelled) return;
      if (result.valid) {
        setStatus('ready');
      } else {
        setStatus(result.expired ? 'expired' : 'invalid');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitAttempted(true);
    setError('');

    if (passwordRules(password).some((r) => !r.met)) return;
    if (password !== confirmPassword) {
      setError(t`Passwords do not match.`);
      return;
    }

    setBusy(true);
    const result = await authService.resetPassword(tokenRef.current!, password);
    setBusy(false);

    if (result.success) {
      setStatus('success');
      setSuccessMessage(result.message || t`Your password has been updated.`);
    } else if (result.invalidLink) {
      setStatus('invalid');
    } else {
      setError(result.error || t`Failed to reset password. Please try again.`);
    }
  };

  const inputClass =
    'w-full px-4 py-2 border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const inputStyle = { backgroundColor: 'var(--color-surface)', color: 'var(--color-text)' };
  const ruleLabels: Record<string, string> = {
    length: t`At least 8 characters`,
    uppercase: t`An uppercase letter`,
    lowercase: t`A lowercase letter`,
    number: t`A number`,
  };

  return (
    <div
      className="p-4 sm:p-8"
      style={{
        fontFamily: "'Inter', sans-serif",
        backgroundColor: 'var(--color-bg)',
        color: 'var(--color-text)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <div className="max-w-md w-full">
        <div className="glass-panel rounded-3xl p-6 sm:p-8 border border-blue-100 dark:border-blue-900/30">
          <div className="text-center mb-6">
            <img
              src={isDark ? '/privacypal-logo-light.png' : '/privacypal-logo.png'}
              alt="PrivacyPal"
              className="w-48 sm:w-64 h-auto prism-edge mx-auto"
            />
          </div>

          {status === 'checking' && (
            <div className="text-center">
              <div className="spinner mx-auto mb-3"></div>
              <p className="text-sm text-slate-600 dark:text-slate-400"><Trans>Checking your reset link…</Trans></p>
            </div>
          )}

          {(status === 'invalid' || status === 'expired') && (
            <div>
              <h2 className="text-xl font-semibold mb-2 text-center">
                {status === 'expired' ? <Trans>Link expired</Trans> : <Trans>Invalid link</Trans>}
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 text-center mb-4">
                {status === 'expired' ? (
                  <Trans>This password reset link has expired — links are valid for 20 minutes.</Trans>
                ) : (
                  <Trans>This password reset link is invalid or has already been used.</Trans>
                )}{' '}
                <Trans>Request a new one below.</Trans>
              </p>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="w-full py-2 bg-slate-900 dark:bg-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                <Trans>Request a new link</Trans>
              </button>
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  <Trans>Back to sign in</Trans>
                </button>
              </div>
            </div>
          )}

          {status === 'ready' && (
            <>
              <h2 className="text-xl font-semibold mb-2 text-center"><Trans>Choose a new password</Trans></h2>
              <p className="text-sm text-slate-500 dark:text-slate-400 text-center mb-6">
                <Trans>Enter and confirm your new password below.</Trans>
              </p>

              {error && (
                <div className="mb-6 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg text-red-700 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2 uppercase tracking-wider"><Trans>New password</Trans></label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className={inputClass}
                      style={inputStyle}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoFocus
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
                      aria-label={showPassword ? t`Hide password` : t`Show password`}
                    >
                      {showPassword ? t`Hide` : t`Show`}
                    </button>
                  </div>
                  <ul className="mt-2 space-y-0.5">
                    {passwordRules(password).map((rule) => {
                      const cls = rule.met
                        ? 'text-emerald-600 dark:text-emerald-400'
                        : submitAttempted
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-slate-500 dark:text-slate-400';
                      return (
                        <li key={rule.id} className={`text-xs flex items-center gap-1.5 ${cls}`}>
                          <span aria-hidden>{rule.met ? '✓' : '○'}</span>
                          {ruleLabels[rule.id]}
                        </li>
                      );
                    })}
                  </ul>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-2 uppercase tracking-wider"><Trans>Confirm new password</Trans></label>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className={inputClass}
                    style={inputStyle}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  {confirmPassword && password !== confirmPassword && (
                    <p className="mt-1.5 text-xs text-red-600 dark:text-red-400"><Trans>Passwords do not match.</Trans></p>
                  )}
                </div>

                <button
                  type="submit"
                  className="w-full py-2 bg-slate-900 dark:bg-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50"
                  disabled={busy}
                >
                  {busy ? t`Updating…` : t`Set new password`}
                </button>
              </form>
            </>
          )}

          {status === 'success' && (
            <div className="text-center">
              <h2 className="text-xl font-semibold mb-2"><Trans>Password updated</Trans></h2>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
                {successMessage}
              </p>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="w-full py-2 bg-slate-900 dark:bg-blue-600 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
              >
                <Trans>Sign in</Trans>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
